import { useMemo, useState } from "react"

import { Flex } from "antd"
import { useTranslation } from "react-i18next"

import FilterCard from "../../../../../../Components/FilterCard"
import InfoTooltip from "../../../../../../Components/InfoTooltip/InfoTooltip"
import {
  CARD_TO_FILTERS,
  CARD_TO_STATUSES,
  isLowEnrichmentPreset,
  LOW_ENRICHMENT_MAX
} from "../../constants/allPageData"

import styles from "./disputesMetricCards.module.less"

// The headline bucket row above the /recover/all table. The cards are multi-select
// toggles over the same filter state the chips write, so this owns which cards are lit
// and hands the resulting filter patch back to the page.
const DisputesMetricCards = ({
  statistics,
  count,
  filters,
  filtersDirty,
  setFilters,
  setPage,
  theme
}) => {
  const { t } = useTranslation()

  // Cards are multi-select TOGGLES: each click adds or removes that card's narrowing,
  // and the card-owned dimensions (status set, enrichment preset, submission bucket) are
  // recomputed as the UNION of every active card. Statuses combine as OR; the enrichment
  // preset and the scheduled bucket AND against that union, matching how the same values
  // behave when set through the filter chips. "All" clears the selection. The card row
  // OWNS those three dimensions: clicking any card rebuilds them from the active set, so
  // a manual chip edit never lingers half-applied behind a card selection.
  const [activeCards, setActiveCards] = useState([])
  const handleCardClick = (cardId) => {
    const next =
      cardId === "all"
        ? []
        : activeCards.includes(cardId)
          ? activeCards.filter((c) => c !== cardId)
          : [...activeCards, cardId]
    setActiveCards(next)
    const statusUnion = new Set()
    let enrichment = null
    let submission = []
    next.forEach((cid) => {
      ;(CARD_TO_STATUSES[cid] || []).forEach((s) => statusUnion.add(s))
      const extra = CARD_TO_FILTERS[cid]
      if (extra?.status) extra.status.forEach((s) => statusUnion.add(s))
      if (extra?.enrichment) enrichment = extra.enrichment
      if (extra?.submission) submission = extra.submission
    })
    setFilters({ status: [...statusUnion], enrichment, submission })
    setPage(1)
  }
  // "All" is the active selection only when nothing at all narrows the list (no cards,
  // no other filter). `filtersDirty` (defined below) covers every non-card filter too,
  // so a highlighted "All" never contradicts a filtered, smaller result set. A toggled
  // card stays lit only while its own narrowing is still present in the filters, so a
  // manual chip edit that removed it also puts the light out (self-healing highlight).
  const cardIsActive = (cardId) => {
    if (cardId === "all") return !filtersDirty && (filters.status || []).length === 0
    if (!activeCards.includes(cardId)) return false
    if (cardId === "low_enrichment") return isLowEnrichmentPreset(filters.enrichment)
    if (cardId === "scheduled") return (filters.submission || []).includes("scheduled")
    const st = filters.status || []
    return (CARD_TO_STATUSES[cardId] || []).every((s) => st.includes(s))
  }

  // Five headline buckets, in the same order as the Status filter anchors. "Lost"
  // counts only disputes we fought and lost (`lost`); "Not fighting" is the Skipped
  // bucket (NOT_FIGHTING), kept separate so a dispute we never fought never inflates
  // the Lost number. Awaiting review / Pending decision stay filter-only.
  const metricCards = useMemo(() => {
    const all = { id: "all", name: t("All"), value: statistics?.all ?? count ?? 0 }
    // Won/Lost collapse into one Closed bucket so the row is not dominated by history,
    // and the cards a merchant can act on come first.
    return [
      all,
      {
        id: "awaiting_review",
        name: t("Needs your review"),
        value: statistics?.awaiting_review ?? 0
      },
      {
        id: "low_enrichment",
        name: t("Low enrichment"),
        value: statistics?.low_enrichment ?? 0,
        // These are the disputes most likely to lose on thin evidence, so the card is
        // marked whenever it has any: warning triangle instead of the info circle, and
        // a warning-toned count. Neutral again at zero, so the row is not permanently
        // shouting.
        attention: (statistics?.low_enrichment ?? 0) > 0,
        // Neither the threshold nor the status limit is self-evident on the label, so
        // both are spelled out here.
        tooltip: t(
          "Open and Awaiting review disputes with an enrichment score of _pct_% or less. Thin evidence weakens the response, so these are worth filling in first.",
          { pct: LOW_ENRICHMENT_MAX }
        )
      },
      { id: "skipped", name: t("Not fighting"), value: statistics?.skipped ?? 0 },
      { id: "scheduled", name: t("Scheduled"), value: statistics?.scheduled ?? 0 },
      {
        id: "closed",
        name: t("Closed"),
        value:
          statistics?.closed ??
          (statistics?.won ?? 0) +
            (statistics?.lost_defended ?? 0) +
            (statistics?.lost_undefended ?? 0),
        // "Closed" merges two statuses, so name them: without this the number looks
        // like it should equal Won, or Lost, rather than their sum.
        tooltip: t("Every dispute that has finished: Won and Lost. Nothing here needs action.")
      }
    ]
  }, [statistics, count, t])

  return (
    <Flex gap={16} wrap="wrap" className={styles.row}>
      {metricCards.map((card) => (
        <div
          key={card.id}
          className={`${styles.rowItem}${card.attention ? ` ${styles.isAttention}` : ""}`}
        >
          <FilterCard
            data={{ ...card, isConnected: true }}
            isActive={cardIsActive(card.id)}
            onClick={handleCardClick}
            theme={theme}
            // A card whose threshold needs explaining puts an info icon in the
            // component's existing top-right cta slot. `null` keeps every other card's
            // slot empty (undefined would fall through to FilterCard's own default).
            cta={
              card.tooltip ? (
                <InfoTooltip
                  title={card.tooltip}
                  icon={card.attention ? "alert_triangle_error" : undefined}
                />
              ) : null
            }
          />
        </div>
      ))}
    </Flex>
  )
}

export default DisputesMetricCards
